import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  Check,
  Home,
  Image as ImageIcon,
  FileText,
  ShoppingBag,
  Monitor,
  Upload,
  Grid,
  Star,
  Video,
  Layers,
  Heart,
  Mail,
  Plus,
  Trash2,
  Quote,
  Users,
  BookOpen,
  PlayCircle,
  Settings,
  Type,
  Edit,
  Info,
  X,
  CheckCircle,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import axios from 'axios';
import EnhancedColorPicker from '../EnhancedColorPicker.jsx';
import EnhancedFontSelector from '../EnhancedFontSelector.jsx';
import Base from '../Base';

// Template Configuration - defines which sections each template needs
const TEMPLATE_SECTIONS = {
  1: ['brand', 'business'],
  2: ['brand', 'categories', 'business'],
  3: ['brand', 'categories', 'banners', 'business'],
  4: ['brand', 'categories', 'hero', 'sliding_banners', 'promo_banners', 'fashion_trends', 'videos', 'business'],
  5: ['brand', 'categories', 'hero', 'sliding_banners', 'promo_banners', 'fashion_trends', 'videos', 'business'],
  6: ['brand', 'categories', 'banners', 'business'],
  7: ['brand', 'categories', 'banners', 'business'],
  8: ['brand', 'categories', 'hero', 'sliding_banners', 'promo_banners', 'fashion_trends', 'videos', 'business'],
  9: ['brand', 'categories', 'hero', 'sliding_banners', 'promo_banners', 'fashion_trends', 'videos', 'business'],
  10: ['brand', 'hero', 'categories', 'business'],
  11: ['brand', 'hero', 'categories', 'collections', 'statement', 'business'],
  12: ['brand', 'hero', 'categories', 'collections', 'statement', 'business'],
  13: ['brand', 'hero', 'categories', 'collections', 'workshop', 'business'],
  14: ['brand', 'hero', 'categories', 'gallery', 'statement', 'instagram', 'business'],
  15: ['brand', 'hero', 'collections_showcase', 'brand_values', 'video_section', 'newsletter', 'business'],
  16: ['brand', 'hero', 'heritage_statement', 'premium_services', 'business'],
  17: ['brand', 'hero_with_values', 'category_showcase', 'craftsmanship_story', 'video_story', 'values_principles', 'newsletter', 'business'],
  18: ['brand', 'hero', 'category_grid', 'quality_values', 'newsletter', 'business'],
  19: ['brand', 'magazine_hero', 'editorial_stories', 'inspirational_quote', 'newsletter', 'business'],
  20: ['brand', 'artist_hero', 'portfolio_gallery', 'artist_statement', 'collections_showcase', 'studio_contact', 'business']
};

// Section metadata
const SECTION_METADATA = {
  brand: { label: 'Brand Information', icon: ShoppingBag },
  hero: { label: 'Hero Section', icon: Monitor },
  hero_with_values: { label: 'Hero with Values', icon: Heart },
  magazine_hero: { label: 'Magazine Hero', icon: BookOpen },
  artist_hero: { label: 'Artist Hero', icon: Users },
  categories: { label: 'Categories', icon: Grid },
  category_showcase: { label: 'Category Showcase', icon: Grid },
  category_grid: { label: 'Category Grid', icon: Grid },
  collections: { label: 'Collections', icon: Layers },
  collections_showcase: { label: 'Collections Showcase', icon: Layers },
  banners: { label: 'Banners', icon: ImageIcon },
  sliding_banners: { label: 'Sliding Banners', icon: ImageIcon },
  promo_banners: { label: 'Promo Banners', icon: ImageIcon },
  fashion_trends: { label: 'Fashion Trends', icon: Star },
  videos: { label: 'Videos', icon: Video },
  video_section: { label: 'Video Section', icon: Video },
  video_story: { label: 'Video Story', icon: PlayCircle },
  newsletter: { label: 'Newsletter', icon: Mail },
  gallery: { label: 'Gallery', icon: ImageIcon },
  portfolio_gallery: { label: 'Portfolio Gallery', icon: ImageIcon },
  instagram: { label: 'Instagram Feed', icon: ImageIcon },
  statement: { label: 'Statement', icon: Quote },
  artist_statement: { label: 'Artist Statement', icon: Quote },
  workshop: { label: 'Workshop', icon: FileText },
  brand_values: { label: 'Brand Values', icon: Heart },
  values_principles: { label: 'Values & Principles', icon: Heart },
  heritage_statement: { label: 'Heritage Statement', icon: Quote },
  premium_services: { label: 'Premium Services', icon: Star },
  craftsmanship_story: { label: 'Craftsmanship Story', icon: Settings },
  quality_values: { label: 'Quality Values', icon: Heart },
  editorial_stories: { label: 'Editorial Stories', icon: FileText },
  inspirational_quote: { label: 'Inspirational Quote', icon: Quote },
  designer_showcase: { label: 'Designer Showcase', icon: Users },
  studio_contact: { label: 'Studio Contact', icon: Mail },
  business: { label: 'Business Information', icon: FileText }
};

const MainPageForm = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [activeSection, setActiveSection] = useState('brand');

  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [previewImages, setPreviewImages] = useState({});

  // Theme state - stores original theme from the template JSON
  const [themeData, setThemeData] = useState(null);

  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    brand: { name: '', logo: '' },
    hero: {
      title: '',
      subtitle: '',
      description: '',
      background_image: '',
      cta_text: 'Shop Collection',
      cta_link: '#products',
      layout_style: 'overlay'
    },
    categories: [
      { title: '', image: '' }
    ],
    collections: [
      { title: '', image: '' }
    ],
    banners: [
      { image: '', description: '' }
    ],
    fashion_trends: [
      { text: '', image: '' }
    ],
    videos: [
      { url: '', title: '' }
    ],
    newsletter: {
      title: 'Stay Updated',
      subtitle: 'Subscribe to our newsletter for exclusive offers',
      enabled: true
    },
    gallery: {
      images: ['', '', '', '', '', '', '', '']
    },
    instagram: {
      images: ['', '', '', '', '', '']
    },
    statement: {
      title: '',
      subtitle: '',
      image: ''
    },
    workshop: {
      title: '',
      description: '',
      image: ''
    },
    brand_values: [
      { title: '', description: '', icon: '' }
    ],
    heritage_statement: {
      title: '',
      description: '',
      image: ''
    },
    premium_services: [
      { title: '', description: '', icon: '' }
    ],
    craftsmanship_story: {
      title: '',
      description: '',
      image: ''
    },
    quality_values: [
      { title: '', description: '' }
    ],
    editorial_stories: [
      { title: '', description: '', image: '' }
    ],
    inspirational_quote: {
      quote: '',
      author: ''
    },
    portfolio_gallery: {
      images: ['', '', '', '', '', '']
    },
    artist_statement: {
      title: '',
      description: '',
      image: ''
    },
    sliding_banners: [
      { image: '', title: '' }
    ],
    promo_banners: [
      { text: '', sub_text: '', image: '' }
    ],
    collections_showcase: {
      title: '',
      description: '',
      category1_title: '',
      category1_count: '',
      category1_image: '',
      category2_title: '',
      category2_count: '',
      category2_image: '',
      category3_title: '',
      category3_count: '',
      category3_image: ''
    },
    video_section: {
      title: '',
      description: '',
      video_url: ''
    },
    hero_with_values: {
      title: '',
      subtitle: '',
      description: '',
      background_image: '',
      cta_text: '',
      cta_link: '',
      value1_label: '',
      value1_description: '',
      value2_label: '',
      value2_description: '',
      value3_label: '',
      value3_description: ''
    },
    category_showcase: {
      title: '',
      description: '',
      category1_title: '',
      category1_image: '',
      category2_title: '',
      category2_image: '',
      category3_title: '',
      category3_image: ''
    },
    video_story: {
      title: '',
      description: '',
      video_url: '',
      stat1_value: '',
      stat1_label: '',
      stat2_value: '',
      stat2_label: ''
    },
    values_principles: {
      title: '',
      description: '',
      principle1_title: '',
      principle1_description: '',
      principle2_title: '',
      principle2_description: '',
      principle3_title: '',
      principle3_description: '',
      principle4_title: '',
      principle4_description: ''
    },
    category_grid: {
      title: '',
      description: '',
      category1_title: '',
      category1_count: '',
      category1_image: '',
      category2_title: '',
      category2_count: '',
      category2_image: '',
      category3_title: '',
      category3_count: '',
      category3_image: '',
      category4_title: '',
      category4_count: '',
      category4_image: ''
    },
    magazine_hero: {
      title: '',
      subtitle: '',
      description: '',
      background_image: '',
      cta_text: '',
      cta_link: ''
    },
    artist_hero: {
      title: '',
      subtitle: '',
      description: '',
      background_image: '',
      cta_text: '',
      cta_link: ''
    },
    studio_contact: {
      title: '',
      address: '',
      email: '',
      phone: ''
    },
    business: {
      contact: {
        business_name: '',
        email: '',
        phone: '',
        working_hours: 'Mon-Sat: 10AM-8PM'
      }
    }
  });

  // Get sections for current template
  const templateSections = TEMPLATE_SECTIONS[parseInt(templateId)] || ['brand', 'hero', 'business'];
  const sections = templateSections.map(sectionId => ({
    id: sectionId,
    ...SECTION_METADATA[sectionId]
  }));

  useEffect(() => {
    loadPageData();
  }, [templateId]);

  useEffect(() => {
    // Set first available section as active
    if (sections.length > 0) {
      setActiveSection(sections[0].id);
    }
  }, [templateId]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/template/${templateId}/page/main`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Loaded page data response:', response.data);

      // Check if there's saved data
      if (response.data.success && response.data.data) {
        const savedData = response.data.data;

        // Extract theme data if available and set it in formData.theme
        if (savedData.theme) {
          console.log('Loading theme data:', savedData.theme);
          setThemeData(savedData.theme);

          // Convert theme to formData format for color pickers
          const themeForForm = {
            colors: {
              primary: Array.isArray(savedData.theme.colors?.primary)
                ? savedData.theme.colors.primary[0]
                : savedData.theme.colors?.primary || '#2C1810',
              secondary: savedData.theme.colors?.secondary || '#ffffff',
              accent: savedData.theme.colors?.accent || '#C9A882',
              background: savedData.theme.colors?.background || '#ffffff',
              text: savedData.theme.colors?.text?.primary || savedData.theme.colors?.text || '#111827',
              surface: savedData.theme.colors?.surface || '#ffffff',
              border: savedData.theme.colors?.border || '#E5E7EB'
            },
            typography: {
              heading: savedData.theme.typography?.heading || 'sans-serif',
              body: savedData.theme.typography?.body || 'sans-serif'
            }
          };

          setFormData(prev => ({
            ...prev,
            theme: themeForForm
          }));
        }

        // If it's v2.0 format JSON, extract the content
        if (savedData.version === '2.0' && savedData.content) {
          console.log('Loading v2.0 format data');
          setFormData(prev => ({ ...prev, ...savedData.content }));
        }
        // If it's old format or direct content
        else if (savedData.content) {
          console.log('Loading content data');
          setFormData(prev => ({ ...prev, ...savedData.content }));
        }
        // If the whole saved data is the form data
        else {
          console.log('Loading saved data directly');
          setFormData(prev => ({ ...prev, ...savedData }));
        }
      }
      // Fallback to old response format
      else if (response.data.content) {
        console.log('Loading old format content');
        setFormData(prev => ({ ...prev, ...response.data.content }));
        // Also check for theme in old format
        if (response.data.theme) {
          setThemeData(response.data.theme);
          // Convert theme to formData format
          const themeForForm = {
            colors: {
              primary: Array.isArray(response.data.theme.colors?.primary)
                ? response.data.theme.colors.primary[0]
                : response.data.theme.colors?.primary || '#2C1810',
              secondary: response.data.theme.colors?.secondary || '#ffffff',
              accent: response.data.theme.colors?.accent || '#C9A882',
              background: response.data.theme.colors?.background || '#ffffff',
              text: response.data.theme.colors?.text?.primary || response.data.theme.colors?.text || '#111827'
            },
            typography: {
              heading: response.data.theme.typography?.heading || 'sans-serif',
              body: response.data.theme.typography?.body || 'sans-serif'
            }
          };
          setFormData(prev => ({ ...prev, theme: themeForForm }));
        }
      }
    } catch (error) {
      console.error('Error loading main page data:', error);
      // Don't show error for 404 - it just means no data saved yet
      if (error.response && error.response.status !== 404) {
        console.error('Unexpected error:', error.response);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (isDraft = true) => {
    try {
      setSaving(true);

      // Get authentication token
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('Please login to save changes');
        navigate('/login');
        return;
      }

      // Check if there are any uploaded files
      const hasFiles = Object.keys(uploadedFiles).length > 0;

      if (hasFiles) {
        // Use FormData for multipart/form-data when files are present
        const formDataToSend = new FormData();
        formDataToSend.append('status', isDraft ? 'draft' : 'complete');
        formDataToSend.append('content', JSON.stringify(formData));

        // Append all uploaded files
        Object.entries(uploadedFiles).forEach(([fieldName, file]) => {
          formDataToSend.append(fieldName, file);
        });

        await axios.post(`/api/template/${templateId}/page/main/save`, formDataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // Use JSON when no files
        await axios.post(`/api/template/${templateId}/page/main/save`, {
          status: isDraft ? 'draft' : 'complete',
          content: formData
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      setLastSaved(new Date());

      // Show success modal
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving:', error);

      // Extract detailed error message
      let errMsg = 'Error saving changes. Please try again.';
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);

        if (error.response.data) {
          if (typeof error.response.data === 'string') {
            errMsg = error.response.data;
          } else if (error.response.data.error) {
            errMsg = error.response.data.error;
          } else if (error.response.data.message) {
            errMsg = error.response.data.message;
          } else if (error.response.data.detail) {
            errMsg = error.response.data.detail;
          }
        }
      } else if (error.request) {
        errMsg = 'No response from server. Please check your connection.';
      } else {
        errMsg = error.message || errMsg;
      }

      setErrorMessage(errMsg);
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close and navigation
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/template-builder/${templateId}`);
  };

  const handleInputChange = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const addArrayItem = (arrayName, defaultItem) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: [...(prev[arrayName] || []), defaultItem]
    }));
  };

  const updateArrayItem = (arrayName, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeArrayItem = (arrayName, index) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const handleFileUpload = (fieldName, file) => {
    if (file) {
      setUploadedFiles(prev => ({
        ...prev,
        [fieldName]: file
      }));

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImages(prev => ({
            ...prev,
            [fieldName]: reader.result
          }));
        };
        reader.readAsDataURL(file);
      }
    }
  };

  // Helper function to get YouTube/Vimeo embed URL
  const getVideoEmbedUrl = (url) => {
    if (!url) return null;

    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Vimeo
    const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    return null;
  };

  // Video Preview Component
  const VideoPreview = ({ url, onRemove }) => {
    const embedUrl = getVideoEmbedUrl(url);

    if (!url) return null;

    return (
      <div className="mt-4 relative">
        {embedUrl ? (
          // YouTube/Vimeo embed
          <div className="aspect-video rounded-lg overflow-hidden border-2 border-gray-200">
            <iframe
              src={embedUrl}
              title="Video preview"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : url.startsWith('http') || url.startsWith('/media') ? (
          // Direct video URL
          <video
            src={url}
            controls
            className="w-full rounded-lg border-2 border-gray-200"
            style={{ maxHeight: '300px' }}
          >
            Your browser does not support the video tag.
          </video>
        ) : null}
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  // Aspect ratio CSS class mapping
  const getAspectRatioClass = (ratio) => {
    const ratioMap = {
      '1:1': 'aspect-square',           // Square (logo, instagram)
      '16:9': 'aspect-video',           // Video/Banner (hero, thumbnails)
      '4:3': 'aspect-[4/3]',            // Landscape
      '3:4': 'aspect-[3/4]',            // Portrait (categories)
      '4:5': 'aspect-[4/5]',            // Portrait+ (products, gallery)
      '2:3': 'aspect-[2/3]',            // Tall portrait (fashion trends)
      '3:2': 'aspect-[3/2]',            // Wide landscape
      '2:1': 'aspect-[2/1]',            // Wide banner (promo)
      '3:1': 'aspect-[3/1]',            // Extra wide (sliders, banners)
      '21:9': 'aspect-[21/9]',          // Ultra-wide (hero backgrounds)
    };
    return ratioMap[ratio] || 'aspect-video'; // Default to 16:9
  };

  const FileUploadArea = ({ fieldName, label, accept = "image/*", required = false, dimensions = "", description = "", existingUrl = "", aspectRatio = "16:9" }) => {
    const previewSrc = previewImages[fieldName] || existingUrl;
    const isVideo = accept.includes('video');
    const aspectClass = getAspectRatioClass(aspectRatio);

    // Get aspect ratio display text
    const getAspectDisplay = (ratio) => {
      const displays = {
        '1:1': 'Square',
        '16:9': 'Landscape',
        '4:3': 'Standard',
        '3:4': 'Portrait',
        '4:5': 'Portrait',
        '2:3': 'Tall',
        '3:2': 'Wide',
        '2:1': 'Banner',
        '3:1': 'Wide Banner',
        '21:9': 'Ultra Wide'
      };
      return displays[ratio] || ratio;
    };

    return (
      <div className="space-y-2">
        {/* Label with badges */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="text-sm font-medium text-gray-700">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <div className="flex items-center gap-2">
            {dimensions && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                {dimensions}
              </span>
            )}
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#D45614]/10 text-[#D45614]">
              {getAspectDisplay(aspectRatio)}
            </span>
          </div>
        </div>
        {description && <p className="text-xs text-gray-500">{description}</p>}

        <div className="relative">
          <input
            type="file"
            accept={accept}
            onChange={(e) => handleFileUpload(fieldName, e.target.files[0])}
            className="hidden"
            id={fieldName}
          />

          {previewSrc && !isVideo ? (
            // Clean image preview
            <div className="relative group">
              <div className={`relative ${aspectClass} w-full max-w-xs overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-50`}>
                <img
                  src={previewSrc}
                  alt="Preview"
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <label
                    htmlFor={fieldName}
                    className="px-3 py-1.5 bg-white text-gray-800 rounded cursor-pointer hover:bg-gray-100 transition-colors text-sm font-medium"
                  >
                    Change
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedFiles(prev => {
                        const newFiles = { ...prev };
                        delete newFiles[fieldName];
                        return newFiles;
                      });
                      setPreviewImages(prev => {
                        const newPreviews = { ...prev };
                        delete newPreviews[fieldName];
                        return newPreviews;
                      });
                    }}
                    className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
                {/* Uploaded indicator */}
                <div className="absolute top-2 right-2 w-6 h-6 bg-[#D45614] rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
          ) : (
            // Clean upload area
            <label htmlFor={fieldName} className="block cursor-pointer">
              <div className={`${aspectClass} max-w-xs border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:border-[#D45614] hover:bg-[#D45614]/5 transition-colors overflow-hidden`}>
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    {isVideo ? (
                      <PlayCircle className="w-6 h-6 text-gray-400" />
                    ) : (
                      <Upload className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-600 text-center">
                    {uploadedFiles[fieldName] ? uploadedFiles[fieldName].name : 'Click to upload'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {isVideo ? 'MP4, WebM, OGG' : 'PNG, JPG, GIF, SVG'}
                  </p>
                </div>
              </div>
            </label>
          )}
        </div>
      </div>
    );
  };

  // Success Modal Component - Vasstr colors
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleSuccessModalClose}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#D45614]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#D45614]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Saved Successfully!</h3>
            <p className="text-gray-600 text-sm mb-6">Your main page configuration has been saved.</p>
            <button onClick={handleSuccessModalClose} className="w-full bg-[#D45614] hover:bg-[#b8480f] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Error Modal Component - Simple design
  const ErrorModal = () => {
    if (!showErrorModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowErrorModal(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <button onClick={() => setShowErrorModal(false)} className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D45614] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading main page...</p>
          </div>
        </div>
      } />
    );
  }

  const FormContent = (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/template-builder/${templateId}`)}
                className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
              <div>
                <h1 className="text-base font-semibold text-gray-900">Main Page - Template {templateId}</h1>
                {lastSaved && (
                  <p className="text-xs text-gray-400">
                    Saved {lastSaved.toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => console.log('Preview:', formData)}
                className="flex items-center gap-2 px-3 py-1.5 text-gray-600 hover:text-[#D45614] transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span className="text-sm">Preview</span>
              </button>
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-1.5 bg-[#D45614] text-white text-sm font-medium hover:bg-[#c04d12] transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Save</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* LEFT SIDEBAR - Fixed width */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 sticky top-24 p-4">
              {/* Progress Summary */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Overall Progress</h4>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${sections.length > 0 ? Math.round((sections.filter(s => formData[s.id] && Object.keys(formData[s.id]).length > 0).length / sections.length) * 100) : 0}%` }}
                  />
                </div>
                <p className="text-xs text-gray-600">
                  {sections.filter(s => formData[s.id] && Object.keys(formData[s.id]).length > 0).length} of {sections.length} sections
                </p>
              </div>

              {/* Page Sections */}
              <h3 className="text-sm font-semibold text-gray-900 mb-3 px-2">Sections</h3>
              <nav className="space-y-1 max-h-[calc(100vh-350px)] overflow-y-auto pr-1">
                {sections.map((section) => {
                  const SectionIcon = section.icon;
                  const isActive = activeSection === section.id;
                  const hasData = formData[section.id] && Object.keys(formData[section.id]).length > 0;

                  return (
                    <button
                      key={section.id}
                      onClick={() => {
                        setActiveSection(section.id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50 border border-transparent hover:border-gray-200'
                      }`}
                    >
                      <SectionIcon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className="flex-1 text-left truncate text-xs leading-tight">{section.label}</span>
                      {hasData && (
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                      )}
                      {isActive && !hasData && (
                        <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* RIGHT CONTENT - Flexible width */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-8">
              {/* Section Header */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {sections.find(s => s.id === activeSection)?.label || 'Section'}
                  </h2>
                  {formData[activeSection] && Object.keys(formData[activeSection]).length > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                      <Check className="w-4 h-4" />
                      Completed
                    </span>
                  )}
                </div>
              </div>

              {/* Brand Section */}
              {activeSection === 'brand' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ShoppingBag className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Identity</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Define your brand's visual identity and core information that will represent your business across your website.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <FileUploadArea
                        fieldName="brand_logo"
                        label="Brand Logo *"
                        accept="image/*"
                        required={true}
                        dimensions="300x300px"
                        description="Your brand logo will appear across your template"
                        existingUrl={formData.brand?.logo || ''}
                        aspectRatio="1:1"
                      />

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Brand Name *
                        </label>
                        <input
                          type="text"
                          value={formData.brand?.name || ''}
                          onChange={(e) => handleInputChange('brand.name', e.target.value)}
                          placeholder="Enter your brand name"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tagline
                        </label>
                        <input
                          type="text"
                          value={formData.brand?.tagline || ''}
                          onChange={(e) => handleInputChange('brand.tagline', e.target.value)}
                          placeholder="Your brand tagline"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Brand Description *
                        </label>
                        <textarea
                          value={formData.brand?.description || ''}
                          onChange={(e) => handleInputChange('brand.description', e.target.value)}
                          placeholder="Describe your brand, mission, and what makes you unique..."
                          rows={5}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">This will be used in your website's about section</p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <EnhancedColorPicker
                          label="Primary Color"
                          value={formData.theme?.colors?.primary || '#2C1810'}
                          onChange={(value) => {
                            const newColors = {
                              ...(formData.theme?.colors || {
                                primary: '#2C1810',
                                secondary: '#ffffff',
                                accent: '#C9A882',
                                background: '#ffffff',
                                text: '#111827'
                              }),
                              primary: value
                            };
                            handleInputChange('theme.colors', newColors);
                          }}
                          presetColors={[
                            '#2C1810', '#4A2F23', '#1A0F0A',
                            '#C9A882', '#E5D4B8', '#A68B6A'
                          ]}
                        />
                        <EnhancedColorPicker
                          label="Secondary Color"
                          value={formData.theme?.colors?.secondary || '#ffffff'}
                          onChange={(value) => {
                            const newColors = {
                              ...(formData.theme?.colors || {
                                primary: '#2C1810',
                                secondary: '#ffffff',
                                accent: '#C9A882',
                                background: '#ffffff',
                                text: '#111827'
                              }),
                              secondary: value
                            };
                            handleInputChange('theme.colors', newColors);
                          }}
                          presetColors={[
                            '#ffffff', '#f8f9fa', '#e9ecef',
                            '#dee2e6', '#ced4da', '#adb5bd'
                          ]}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <EnhancedColorPicker
                          label="Accent Color"
                          value={formData.theme?.colors?.accent || '#C9A882'}
                          onChange={(value) => {
                            const newColors = {
                              ...(formData.theme?.colors || {
                                primary: '#2C1810',
                                secondary: '#ffffff',
                                accent: '#C9A882',
                                background: '#ffffff',
                                text: '#111827'
                              }),
                              accent: value
                            };
                            handleInputChange('theme.colors', newColors);
                          }}
                          presetColors={[
                            '#C9A882', '#E5D4B8', '#A68B6A',
                            '#D4AF37', '#FFD700', '#B8860B'
                          ]}
                        />
                        <EnhancedColorPicker
                          label="Text Color"
                          value={formData.theme?.colors?.text || '#111827'}
                          onChange={(value) => {
                            const newColors = {
                              ...(formData.theme?.colors || {
                                primary: '#2C1810',
                                secondary: '#ffffff',
                                accent: '#C9A882',
                                background: '#ffffff',
                                text: '#111827'
                              }),
                              text: value
                            };
                            handleInputChange('theme.colors', newColors);
                          }}
                          presetColors={[
                            '#111827', '#1f2937', '#374151',
                            '#4b5563', '#6b7280', '#9ca3af'
                          ]}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <EnhancedFontSelector
                          label="Heading Font"
                          value={formData.theme?.typography?.heading || formData.theme?.fonts?.heading || 'sans-serif'}
                          onChange={(value) => handleInputChange('theme.typography.heading', value)}
                          category="all"
                        />
                        <EnhancedFontSelector
                          label="Body Font"
                          value={formData.theme?.typography?.body || formData.theme?.fonts?.body || 'sans-serif'}
                          onChange={(value) => handleInputChange('theme.typography.body', value)}
                          category="all"
                        />
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                          <Info className="w-4 h-4 mr-2" />
                          Owner Information (Optional)
                        </h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Owner Name
                            </label>
                            <input
                              type="text"
                              value={formData.brand?.owner?.name || ''}
                              onChange={(e) => handleInputChange('brand.owner.name', e.target.value)}
                              placeholder="Your name"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Owner Bio
                            </label>
                            <textarea
                              value={formData.brand?.owner?.bio || ''}
                              onChange={(e) => handleInputChange('brand.owner.bio', e.target.value)}
                              placeholder="Brief bio about the owner/founder"
                              rows={3}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hero Section */}
              {activeSection === 'hero' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Monitor className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Hero Section</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Create an impactful first impression with your hero section that captures visitors' attention immediately.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hero Title *</label>
                        <input
                          type="text"
                          value={formData.hero?.title || ''}
                          onChange={(e) => handleInputChange('hero.title', e.target.value)}
                          placeholder="e.g., Welcome to Fashion Excellence"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">Main headline that visitors see first</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hero Subtitle</label>
                        <input
                          type="text"
                          value={formData.hero?.subtitle || ''}
                          onChange={(e) => handleInputChange('hero.subtitle', e.target.value)}
                          placeholder="e.g., Discover timeless elegance"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                        <p className="mt-1 text-xs text-gray-500">Supporting text under the main title</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                          value={formData.hero?.description || ''}
                          onChange={(e) => handleInputChange('hero.description', e.target.value)}
                          placeholder="Additional descriptive text about your brand or current offers..."
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Call-to-Action Text</label>
                          <input
                            type="text"
                            value={formData.hero?.cta_text || 'Shop Collection'}
                            onChange={(e) => handleInputChange('hero.cta_text', e.target.value)}
                            placeholder="Shop Now"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link</label>
                          <input
                            type="text"
                            value={formData.hero?.cta_link || '#products'}
                            onChange={(e) => handleInputChange('hero.cta_link', e.target.value)}
                            placeholder="#products"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <FileUploadArea
                        fieldName="hero_background"
                        label="Hero Background Image *"
                        accept="image/*"
                        required={true}
                        dimensions="1920x800px recommended"
                        description="High-quality background image for your hero section"
                        existingUrl={formData.hero?.background_image || formData.hero?.backgroundImage || ''}
                        aspectRatio="21:9"
                      />

                      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                        <h4 className="font-semibold text-purple-900 mb-3">Hero Style Options</h4>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hero Layout Style</label>
                        <select
                          value={formData.layout?.heroStyle || 'overlay'}
                          onChange={(e) => handleInputChange('layout.heroStyle', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                        >
                          <option value="minimal">Minimal - Clean and simple</option>
                          <option value="split">Split - Text and image side by side</option>
                          <option value="overlay">Overlay - Text over background image</option>
                          <option value="fullscreen">Fullscreen - Full viewport height</option>
                          <option value="slider">Slider - Multiple hero slides</option>
                          <option value="video">Video - Video background</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Categories Section */}
              {activeSection === 'categories' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Layers className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Product Categories</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Create beautiful product categories to organize your inventory and help customers find what they're looking for.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {formData.categories?.map((category, index) => (
                      <div key={index} className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {category.name || `Category ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('categories', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete category"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Category Name *</label>
                              <input
                                type="text"
                                value={category.name || ''}
                                onChange={(e) => updateArrayItem('categories', index, 'name', e.target.value)}
                                placeholder="e.g., Women's Clothing"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                              <input
                                type="text"
                                value={category.description || ''}
                                onChange={(e) => updateArrayItem('categories', index, 'description', e.target.value)}
                                placeholder="Brief description"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                              <select
                                value={category.gender || 'All'}
                                onChange={(e) => updateArrayItem('categories', index, 'gender', e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              >
                                <option value="All">All Genders</option>
                                <option value="Men">Men</option>
                                <option value="Women">Women</option>
                                <option value="Kids">Kids</option>
                              </select>
                            </div>

                            <div className="md:col-span-2 lg:col-span-3">
                              <FileUploadArea
                                fieldName={`category_image_${index}`}
                                label="Category Image"
                                accept="image/*"
                                dimensions="400x500px recommended"
                                description="High-quality image representing this category"
                                existingUrl={category.image || ''}
                                aspectRatio="4:5"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('categories', { name: '', description: '', gender: 'All', image: '' })}
                      className="w-full border-2 border-dashed border-blue-300 rounded-2xl p-8 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-blue-400 group-hover:text-blue-600 mb-3" />
                      <span className="text-blue-600 group-hover:text-blue-700 font-medium">
                        Add Category
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Collections Section */}
              {activeSection === 'collections' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Grid className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Collections</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your featured product collections and highlights</p>
                  </div>

                  <div className="space-y-6">
                    {formData.collections?.map((collection, index) => (
                      <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {collection.name || `Collection ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('collections', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete collection"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Collection Name *</label>
                              <input
                                type="text"
                                value={collection.name || ''}
                                onChange={(e) => updateArrayItem('collections', index, 'name', e.target.value)}
                                placeholder="e.g., Elite Collection"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                              <select
                                value={collection.gender || 'All'}
                                onChange={(e) => updateArrayItem('collections', index, 'gender', e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              >
                                <option value="All">All Genders</option>
                                <option value="Men">Men</option>
                                <option value="Women">Women</option>
                                <option value="Kids">Kids</option>
                              </select>
                            </div>

                            <div className="md:col-span-2">
                              <FileUploadArea
                                fieldName={`collection_image_${index}`}
                                label="Collection Image"
                                accept="image/*"
                                dimensions="800x600px recommended"
                                description="Main image for this collection"
                                existingUrl={collection.image || ''}
                                aspectRatio="4:3"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('collections', { name: '', gender: 'All', image: '' })}
                      className="w-full border-2 border-dashed border-purple-300 rounded-2xl p-8 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-purple-400 group-hover:text-purple-600 mb-3" />
                      <span className="text-purple-600 group-hover:text-purple-700 font-medium">
                        Add Collection
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Videos Section */}
              {activeSection === 'videos' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <PlayCircle className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Video Gallery</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Add engaging video content to showcase your products and brand story.
                    </p>
                  </div>

                  <div className="space-y-6">
                    {formData.videos?.map((video, index) => (
                      <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {video.title || `Video ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('videos', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete video"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Video Title *</label>
                              <input
                                type="text"
                                value={video.title || ''}
                                onChange={(e) => updateArrayItem('videos', index, 'title', e.target.value)}
                                placeholder="e.g., Brand Story"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                              <textarea
                                value={video.description || ''}
                                onChange={(e) => updateArrayItem('videos', index, 'description', e.target.value)}
                                placeholder="Brief description of the video content..."
                                rows={2}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Video URL (YouTube/Vimeo)</label>
                              <input
                                type="url"
                                value={video.url || ''}
                                onChange={(e) => updateArrayItem('videos', index, 'url', e.target.value)}
                                placeholder="https://youtube.com/watch?v=..."
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                              <p className="mt-1 text-xs text-gray-500">Or upload a video file below</p>
                              {/* Video Preview for URL */}
                              {video.url && (
                                <VideoPreview
                                  url={video.url}
                                  onRemove={() => updateArrayItem('videos', index, 'url', '')}
                                />
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Video Thumbnail</label>
                              <FileUploadArea
                                fieldName={`video_thumbnail_${index}`}
                                label="Upload Thumbnail"
                                accept="image/*"
                                dimensions="640x360px recommended"
                                description="Thumbnail image for the video"
                                existingUrl={video.thumbnail || ''}
                                aspectRatio="16:9"
                              />
                            </div>

                            <FileUploadArea
                              fieldName={`video_file_${index}`}
                              label="Upload Video File (Optional)"
                              accept="video/*"
                              description="Upload video file directly (MP4, WebM, or OGG)"
                              existingUrl={video.file || ''}
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('videos', { title: '', description: '', url: '', file: '' })}
                      className="w-full border-2 border-dashed border-purple-300 rounded-2xl p-8 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-purple-400 group-hover:text-purple-600 mb-3" />
                      <span className="text-purple-600 group-hover:text-purple-700 font-medium">
                        Add Video
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Newsletter Section */}
              {activeSection === 'newsletter' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Mail className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Newsletter Signup</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Encourage visitors to subscribe to your newsletter.</p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title *</label>
                        <input
                          type="text"
                          value={formData.newsletter?.title || ''}
                          onChange={(e) => handleInputChange('newsletter.title', e.target.value)}
                          placeholder="e.g., Stay connected"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                        <textarea
                          value={formData.newsletter?.description || ''}
                          onChange={(e) => handleInputChange('newsletter.description', e.target.value)}
                          placeholder="Tell visitors about your newsletter benefits..."
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Button Text</label>
                        <input
                          type="text"
                          value={formData.newsletter?.button_text || ''}
                          onChange={(e) => handleInputChange('newsletter.button_text', e.target.value)}
                          placeholder="e.g., Subscribe, Join, Sign Up"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Placeholder Text</label>
                        <input
                          type="text"
                          value={formData.newsletter?.placeholder_text || ''}
                          onChange={(e) => handleInputChange('newsletter.placeholder_text', e.target.value)}
                          placeholder="e.g., Your email address"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Privacy Note (optional)</label>
                        <textarea
                          value={formData.newsletter?.privacy_note || ''}
                          onChange={(e) => handleInputChange('newsletter.privacy_note', e.target.value)}
                          placeholder="e.g., We respect your privacy. Unsubscribe at any time."
                          rows={2}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>

                    <div>
                      <FileUploadArea
                        fieldName="newsletter_background"
                        label="Background Image (Optional)"
                        accept="image/*"
                        dimensions="1200x600px"
                        description="Background image for newsletter section"
                        existingUrl={formData.newsletter?.background_image || ''}
                        aspectRatio="2:1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Business Information Section */}
              {activeSection === 'business' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Business Information</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      Provide your business contact details and information for customers to reach you.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                        <input
                          type="text"
                          value={formData.business?.contact?.business_name || ''}
                          onChange={(e) => handleInputChange('business.contact.business_name', e.target.value)}
                          placeholder="Official business name"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                        <input
                          type="email"
                          value={formData.business?.contact?.email || ''}
                          onChange={(e) => handleInputChange('business.contact.email', e.target.value)}
                          placeholder="contact@yourbusiness.com"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          value={formData.business?.contact?.phone || ''}
                          onChange={(e) => handleInputChange('business.contact.phone', e.target.value)}
                          placeholder="+1 (555) 123-4567"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Working Hours</label>
                        <input
                          type="text"
                          value={formData.business?.contact?.working_hours || 'Mon-Sat: 10AM-8PM'}
                          onChange={(e) => handleInputChange('business.contact.working_hours', e.target.value)}
                          placeholder="Mon-Fri 9AM-6PM"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Business Address</label>
                        <textarea
                          value={formData.business?.contact?.address || ''}
                          onChange={(e) => handleInputChange('business.contact.address', e.target.value)}
                          placeholder="123 Business Street, City, State, ZIP"
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Website URL</label>
                        <input
                          type="url"
                          value={formData.business?.contact?.website || ''}
                          onChange={(e) => handleInputChange('business.contact.website', e.target.value)}
                          placeholder="https://www.yourbusiness.com"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Designer Showcase Section */}
              {activeSection === 'designer_showcase' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Designer Showcase</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Feature your talented design team and their expertise.</p>
                  </div>

                  <div className="space-y-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Section Title *</label>
                        <input
                          type="text"
                          value={formData.designer_showcase?.title || ''}
                          onChange={(e) => handleInputChange('designer_showcase.title', e.target.value)}
                          placeholder="e.g., Meet Our Designers"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                        <textarea
                          value={formData.designer_showcase?.description || ''}
                          onChange={(e) => handleInputChange('designer_showcase.description', e.target.value)}
                          placeholder="Introduce your design team..."
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <div key={num} className="bg-gray-50 p-6 rounded-lg">
                          <h4 className="text-lg font-semibold mb-4">Designer {num}</h4>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                              <input
                                type="text"
                                value={formData.designer_showcase?.[`designer${num}_name`] || ''}
                                onChange={(e) => handleInputChange(`designer_showcase.designer${num}_name`, e.target.value)}
                                placeholder="Designer Name"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>
                            <FileUploadArea
                              fieldName={`designer${num}_image`}
                              label="Designer Photo"
                              accept="image/*"
                              dimensions="150x150px"
                              description="Profile photo"
                              aspectRatio="1:1"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Statement Section */}
              {activeSection === 'statement' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-rose-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Quote className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Statement</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create powerful brand messaging that resonates with your audience.</p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Main Statement *</label>
                        <textarea
                          value={formData.statement?.title || ''}
                          onChange={(e) => handleInputChange('statement.title', e.target.value)}
                          placeholder="Write your brand's main statement or mission..."
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Supporting Text</label>
                        <textarea
                          value={formData.statement?.subtitle || ''}
                          onChange={(e) => handleInputChange('statement.subtitle', e.target.value)}
                          placeholder="Additional supporting text for your statement..."
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                    <div>
                      <FileUploadArea
                        fieldName="statement_background_image"
                        label="Statement Background Image"
                        accept="image/*"
                        dimensions="1920x800px"
                        description="Background image for the statement section"
                        existingUrl={formData.statement?.background_image || formData.statement?.backgroundImage || ''}
                        aspectRatio="21:9"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Workshop Section */}
              {activeSection === 'workshop' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-amber-600 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Settings className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Workshop Information</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your workshop, studio, or behind-the-scenes content.</p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Workshop Title *</label>
                        <input
                          type="text"
                          value={formData.workshop?.title || ''}
                          onChange={(e) => handleInputChange('workshop.title', e.target.value)}
                          placeholder="e.g., Our Artisan Workshop"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                        <input
                          type="text"
                          value={formData.workshop?.location || ''}
                          onChange={(e) => handleInputChange('workshop.location', e.target.value)}
                          placeholder="e.g., Mumbai, India"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Workshop Description</label>
                        <textarea
                          value={formData.workshop?.description || ''}
                          onChange={(e) => handleInputChange('workshop.description', e.target.value)}
                          placeholder="Describe your workshop, processes, and craftsmanship..."
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                        />
                      </div>
                    </div>
                    <div>
                      <FileUploadArea
                        fieldName="workshop_image"
                        label="Workshop Image"
                        accept="image/*"
                        dimensions="800x600px"
                        description="Image showcasing your workshop"
                        existingUrl={formData.workshop?.image || ''}
                        aspectRatio="4:3"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Banners Section */}
              {activeSection === 'banners' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Banners</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Add promotional banners to your homepage</p>
                  </div>

                  <div className="space-y-6">
                    {formData.banners?.map((banner, index) => (
                      <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {banner.name || `Banner ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('banners', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete banner"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Banner Name *</label>
                              <input
                                type="text"
                                value={banner.name || ''}
                                onChange={(e) => updateArrayItem('banners', index, 'name', e.target.value)}
                                placeholder="e.g., Summer Collection"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                              <input
                                type="text"
                                value={banner.description || ''}
                                onChange={(e) => updateArrayItem('banners', index, 'description', e.target.value)}
                                placeholder="Brief description"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>
                          </div>

                          <FileUploadArea
                            fieldName={`banner_image_${index}`}
                            label="Banner Image"
                            accept="image/*"
                            dimensions="1200x400px recommended"
                            description="High-quality banner image"
                            existingUrl={banner.image || ''}
                            aspectRatio="3:1"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('banners', { name: '', description: '', image: '' })}
                      className="w-full border-2 border-dashed border-blue-300 rounded-2xl p-8 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-blue-400 group-hover:text-blue-600 mb-3" />
                      <span className="text-blue-600 group-hover:text-blue-700 font-medium">
                        Add Banner
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Sliding Banners Section */}
              {activeSection === 'sliding_banners' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Layers className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Sliding Banners</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Add carousel banners for your homepage</p>
                  </div>

                  <div className="space-y-6">
                    {formData.sliding_banners?.map((banner, index) => (
                      <div key={index} className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {banner.title || `Slide ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('sliding_banners', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete slide"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Banner Title *</label>
                            <input
                              type="text"
                              value={banner.title || ''}
                              onChange={(e) => updateArrayItem('sliding_banners', index, 'title', e.target.value)}
                              placeholder="e.g., New Collection Launch"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              required
                            />
                          </div>

                          <FileUploadArea
                            fieldName={`sliding_banner_image_${index}`}
                            label="Sliding Banner Image"
                            accept="image/*"
                            dimensions="1920x600px recommended"
                            description="High-resolution banner for carousel"
                            existingUrl={banner.image || ''}
                            aspectRatio="3:1"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('sliding_banners', { title: '', image: '' })}
                      className="w-full border-2 border-dashed border-indigo-300 rounded-2xl p-8 hover:border-indigo-400 hover:bg-indigo-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-indigo-400 group-hover:text-indigo-600 mb-3" />
                      <span className="text-indigo-600 group-hover:text-indigo-700 font-medium">
                        Add Slide
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Promo Banners Section */}
              {activeSection === 'promo_banners' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-orange-600 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Promotional Banners</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Add special promotional banners</p>
                  </div>

                  <div className="space-y-6">
                    {formData.promo_banners?.map((promo, index) => (
                      <div key={index} className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {promo.text || `Promo ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('promo_banners', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete promo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Promo Text *</label>
                              <input
                                type="text"
                                value={promo.text || ''}
                                onChange={(e) => updateArrayItem('promo_banners', index, 'text', e.target.value)}
                                placeholder="e.g., Summer Sale"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Sub Text</label>
                              <input
                                type="text"
                                value={promo.sub_text || ''}
                                onChange={(e) => updateArrayItem('promo_banners', index, 'sub_text', e.target.value)}
                                placeholder="e.g., Up to 60% Off"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>
                          </div>

                          <FileUploadArea
                            fieldName={`promo_banner_image_${index}`}
                            label="Promo Banner Image"
                            accept="image/*"
                            dimensions="800x400px recommended"
                            description="Promotional banner image"
                            existingUrl={promo.image || ''}
                            aspectRatio="2:1"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('promo_banners', { text: '', sub_text: '', image: '' })}
                      className="w-full border-2 border-dashed border-orange-300 rounded-2xl p-8 hover:border-orange-400 hover:bg-orange-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-orange-400 group-hover:text-orange-600 mb-3" />
                      <span className="text-orange-600 group-hover:text-orange-700 font-medium">
                        Add Promo Banner
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Fashion Trends Section */}
              {activeSection === 'fashion_trends' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-pink-600 to-rose-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Fashion Trends</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase the latest fashion trends</p>
                  </div>

                  <div className="space-y-6">
                    {formData.fashion_trends?.map((trend, index) => (
                      <div key={index} className="bg-gradient-to-br from-pink-50 to-rose-50 border border-pink-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-pink-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {trend.text || `Trend ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('fashion_trends', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete trend"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Trend Name *</label>
                              <input
                                type="text"
                                value={trend.text || ''}
                                onChange={(e) => updateArrayItem('fashion_trends', index, 'text', e.target.value)}
                                placeholder="e.g., Minimalist Chic"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                              <select
                                value={trend.gender || 'All'}
                                onChange={(e) => updateArrayItem('fashion_trends', index, 'gender', e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              >
                                <option value="All">All Genders</option>
                                <option value="Men">Men</option>
                                <option value="Women">Women</option>
                                <option value="Kids">Kids</option>
                              </select>
                            </div>
                          </div>

                          <FileUploadArea
                            fieldName={`fashion_trend_image_${index}`}
                            label="Trend Image"
                            accept="image/*"
                            dimensions="400x600px recommended"
                            description="Image showcasing this fashion trend"
                            existingUrl={trend.image || ''}
                            aspectRatio="2:3"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('fashion_trends', { text: '', gender: 'All', image: '' })}
                      className="w-full border-2 border-dashed border-pink-300 rounded-2xl p-8 hover:border-pink-400 hover:bg-pink-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-pink-400 group-hover:text-pink-600 mb-3" />
                      <span className="text-pink-600 group-hover:text-pink-700 font-medium">
                        Add Trend
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Gallery Section */}
              {activeSection === 'gallery' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Gallery</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Create a beautiful image gallery</p>
                  </div>

                  <div className="space-y-6">
                    {formData.gallery?.map((item, index) => (
                      <div key={index} className="bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {item.title || `Gallery Image ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('gallery', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete image"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Image Title *</label>
                            <input
                              type="text"
                              value={item.title || ''}
                              onChange={(e) => updateArrayItem('gallery', index, 'title', e.target.value)}
                              placeholder="e.g., Portrait Series"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              required
                            />
                          </div>

                          <FileUploadArea
                            fieldName={`gallery_image_${index}`}
                            label="Gallery Image"
                            accept="image/*"
                            dimensions="600x750px recommended"
                            description={`Gallery image ${index + 1}`}
                            existingUrl={item.image || ''}
                            aspectRatio="4:5"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('gallery', { title: '', image: '' })}
                      className="w-full border-2 border-dashed border-teal-300 rounded-2xl p-8 hover:border-teal-400 hover:bg-teal-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-teal-400 group-hover:text-teal-600 mb-3" />
                      <span className="text-teal-600 group-hover:text-teal-700 font-medium">
                        Add Image
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Instagram Section */}
              {activeSection === 'instagram' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Instagram Feed</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your Instagram posts</p>
                  </div>

                  <div className="space-y-6">
                    {formData.instagram?.map((post, index) => (
                      <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {post.caption || `Post ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('instagram', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete post"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Caption *</label>
                            <input
                              type="text"
                              value={post.caption || ''}
                              onChange={(e) => updateArrayItem('instagram', index, 'caption', e.target.value)}
                              placeholder="e.g., Daily inspiration"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              required
                            />
                          </div>

                          <FileUploadArea
                            fieldName={`instagram_image_${index}`}
                            label="Instagram Image"
                            accept="image/*"
                            dimensions="1080x1080px recommended"
                            description="Square image for Instagram feed"
                            existingUrl={post.image || ''}
                            aspectRatio="1:1"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('instagram', { caption: '', image: '' })}
                      className="w-full border-2 border-dashed border-purple-300 rounded-2xl p-8 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-purple-400 group-hover:text-purple-600 mb-3" />
                      <span className="text-purple-600 group-hover:text-purple-700 font-medium">
                        Add Post
                      </span>
                    </button>
                  </div>
                </div>
              )}



              {/* Banners Section */}
              {activeSection === 'banners' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Banners</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Add promotional banners to your homepage</p>
                  </div>

                  <div className="space-y-6">
                    {formData.banners?.map((banner, index) => (
                      <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {banner.name || `Banner ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('banners', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete banner"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Banner Name *</label>
                              <input
                                type="text"
                                value={banner.name || ''}
                                onChange={(e) => updateArrayItem('banners', index, 'name', e.target.value)}
                                placeholder="e.g., Summer Collection"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                              <input
                                type="text"
                                value={banner.description || ''}
                                onChange={(e) => updateArrayItem('banners', index, 'description', e.target.value)}
                                placeholder="Brief description"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              />
                            </div>
                          </div>

                          <FileUploadArea
                            fieldName={`banner_image_${index}`}
                            label="Banner Image"
                            accept="image/*"
                            dimensions="1200x400px recommended"
                            description="High-quality banner image"
                            existingUrl={banner.image || ''}
                            aspectRatio="3:1"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('banners', { name: '', description: '', image: '' })}
                      className="w-full border-2 border-dashed border-blue-300 rounded-2xl p-8 hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-blue-400 group-hover:text-blue-600 mb-3" />
                      <span className="text-blue-600 group-hover:text-blue-700 font-medium">
                        Add Banner
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* NOTE: Duplicate sliding_banners, promo_banners, fashion_trends, gallery sections removed */}


              {/* Instagram Section */}
              {activeSection === 'instagram' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ImageIcon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Instagram Feed</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Showcase your Instagram posts</p>
                  </div>

                  <div className="space-y-6">
                    {formData.instagram?.map((post, index) => (
                      <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex justify-between items-center p-6 pb-4">
                          <div className="flex items-center space-x-3">
                            <span className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {post.caption || `Post ${index + 1}`}
                            </h4>
                          </div>
                          <button
                            onClick={() => removeArrayItem('instagram', index)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete post"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="px-6 pb-6">
                          <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Caption *</label>
                            <input
                              type="text"
                              value={post.caption || ''}
                              onChange={(e) => updateArrayItem('instagram', index, 'caption', e.target.value)}
                              placeholder="e.g., Daily inspiration"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
                              required
                            />
                          </div>

                          <FileUploadArea
                            fieldName={`instagram_image_${index}`}
                            label="Instagram Image"
                            accept="image/*"
                            dimensions="1080x1080px recommended"
                            description="Square image for Instagram feed"
                            existingUrl={post.image || ''}
                            aspectRatio="1:1"
                          />
                        </div>
                      </div>
                    ))}

                    <button
                      onClick={() => addArrayItem('instagram', { caption: '', image: '' })}
                      className="w-full border-2 border-dashed border-purple-300 rounded-2xl p-8 hover:border-purple-400 hover:bg-purple-50 transition-all duration-300 group"
                    >
                      <Plus className="w-8 h-8 mx-auto text-purple-400 group-hover:text-purple-600 mb-3" />
                      <span className="text-purple-600 group-hover:text-purple-700 font-medium">
                        Add Post
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* Collections Showcase Section */}
              {activeSection === 'collections_showcase' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Collections Showcase</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Display your collections with elegant category navigation.</p>
                  </div>
                  <div className="space-y-6">
                    <input type="text" value={formData.collections_showcase?.title || ''} onChange={(e) => handleInputChange('collections_showcase.title', e.target.value)} placeholder="e.g., Collections" className="w-full px-4 py-3 border rounded-lg" />
                    <textarea value={formData.collections_showcase?.description || ''} onChange={(e) => handleInputChange('collections_showcase.description', e.target.value)} placeholder="Describe your collections..." rows={3} className="w-full px-4 py-3 border rounded-lg" />
                    {[1, 2, 3].map((num) => (<div key={num} className="bg-gray-50 p-6 rounded-lg"><h5>Category {num}</h5><input type="text" value={formData.collections_showcase?.[`category${num}_title`] || ''} onChange={(e) => handleInputChange(`collections_showcase.category${num}_title`, e.target.value)} placeholder="Category Title" className="w-full px-4 py-3 border rounded-lg mb-4" /><input type="text" value={formData.collections_showcase?.[`category${num}_count`] || ''} onChange={(e) => handleInputChange(`collections_showcase.category${num}_count`, e.target.value)} placeholder="Item Count" className="w-full px-4 py-3 border rounded-lg mb-4" /><FileUploadArea fieldName={`collections_category${num}_image`} label="Category Image" accept="image/*" dimensions="600x800px" aspectRatio="3:4" /></div>))}
                  </div>
                </div>
              )}

              {/* Brand Values Section */}
              {activeSection === 'brand_values' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Brand Values</h2>
                  </div>
                  <input type="text" value={formData.brand_values?.title || ''} onChange={(e) => handleInputChange('brand_values.title', e.target.value)} placeholder="Section Title" className="w-full px-4 py-3 border rounded-lg mb-4" />
                  <textarea value={formData.brand_values?.description || ''} onChange={(e) => handleInputChange('brand_values.description', e.target.value)} rows={4} placeholder="Describe your brand values..." className="w-full px-4 py-3 border rounded-lg mb-4" />
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">{[1, 2, 3].map((num) => (<div key={num} className="bg-gray-50 p-6 rounded-lg"><h5>Value {num}</h5><input type="text" value={formData.brand_values?.[`value${num}_label`] || ''} onChange={(e) => handleInputChange(`brand_values.value${num}_label`, e.target.value)} placeholder="Label" className="w-full px-4 py-3 border rounded-lg mb-2" /><input type="text" value={formData.brand_values?.[`value${num}_value`] || ''} onChange={(e) => handleInputChange(`brand_values.value${num}_value`, e.target.value)} placeholder="Value" className="w-full px-4 py-3 border rounded-lg mb-2" /><textarea value={formData.brand_values?.[`value${num}_description`] || ''} onChange={(e) => handleInputChange(`brand_values.value${num}_description`, e.target.value)} rows={2} placeholder="Description" className="w-full px-4 py-3 border rounded-lg" /></div>))}</div>
                </div>
              )}

              {/* Video Section */}
              {activeSection === 'video_section' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Our Story Video</h2>
                  </div>
                  <input type="text" value={formData.video_section?.title || ''} onChange={(e) => handleInputChange('video_section.title', e.target.value)} placeholder="Video Title" className="w-full px-4 py-3 border rounded-lg mb-4" />
                  <textarea value={formData.video_section?.description || ''} onChange={(e) => handleInputChange('video_section.description', e.target.value)} rows={3} placeholder="Video Description" className="w-full px-4 py-3 border rounded-lg mb-4" />
                  <FileUploadArea fieldName="video_section_thumbnail" label="Video Thumbnail" accept="image/*" dimensions="1200x675px" aspectRatio="16:9" />
                </div>
              )}

              {/* Heritage Statement */}
              {activeSection === 'heritage_statement' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Heritage Statement</h2>
                  </div>
                  <input type="text" value={formData.heritage_statement?.title || ''} onChange={(e) => handleInputChange('heritage_statement.title', e.target.value)} placeholder="Statement Title" className="w-full px-4 py-3 border rounded-lg mb-4" />
                  <textarea value={formData.heritage_statement?.description || ''} onChange={(e) => handleInputChange('heritage_statement.description', e.target.value)} rows={5} placeholder="Heritage Description" className="w-full px-4 py-3 border rounded-lg" />
                </div>
              )}

              {/* Premium Services */}
              {activeSection === 'premium_services' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Premium Services</h2>
                  </div>
                  <input type="text" value={formData.premium_services?.title || ''} onChange={(e) => handleInputChange('premium_services.title', e.target.value)} placeholder="Section Title" className="w-full px-4 py-3 border rounded-lg mb-4" />
                  <textarea value={formData.premium_services?.description || ''} onChange={(e) => handleInputChange('premium_services.description', e.target.value)} rows={3} placeholder="Description" className="w-full px-4 py-3 border rounded-lg mb-4" />
                  {[1, 2, 3].map((num) => (<div key={num} className="bg-gray-50 p-6 rounded-lg mb-4"><h5>Service {num}</h5><input type="text" value={formData.premium_services?.[`service${num}_title`] || ''} onChange={(e) => handleInputChange(`premium_services.service${num}_title`, e.target.value)} placeholder="Service Title" className="w-full px-4 py-3 border rounded-lg mb-2" /><textarea value={formData.premium_services?.[`service${num}_description`] || ''} onChange={(e) => handleInputChange(`premium_services.service${num}_description`, e.target.value)} rows={3} placeholder="Service Description" className="w-full px-4 py-3 border rounded-lg" /></div>))}
                </div>
              )}

              {/* Hero with Values */}
              {activeSection === 'hero_with_values' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Hero with Values</h2>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <input type="text" value={formData.hero_with_values?.title || ''} onChange={(e) => handleInputChange('hero_with_values.title', e.target.value)} placeholder="Main Title" className="w-full px-4 py-3 border rounded-lg" />
                      <input type="text" value={formData.hero_with_values?.subtitle || ''} onChange={(e) => handleInputChange('hero_with_values.subtitle', e.target.value)} placeholder="Subtitle" className="w-full px-4 py-3 border rounded-lg" />
                      <textarea value={formData.hero_with_values?.description || ''} onChange={(e) => handleInputChange('hero_with_values.description', e.target.value)} rows={4} placeholder="Description" className="w-full px-4 py-3 border rounded-lg" />
                      <div className="grid grid-cols-2 gap-4">
                        <input type="text" value={formData.hero_with_values?.button1_text || ''} onChange={(e) => handleInputChange('hero_with_values.button1_text', e.target.value)} placeholder="Button 1 Text" className="w-full px-4 py-3 border rounded-lg" />
                        <input type="text" value={formData.hero_with_values?.button2_text || ''} onChange={(e) => handleInputChange('hero_with_values.button2_text', e.target.value)} placeholder="Button 2 Text" className="w-full px-4 py-3 border rounded-lg" />
                      </div>
                      {[1, 2, 3].map((num) => (<div key={num} className="bg-gray-50 p-4 rounded-lg"><h5>Value {num}</h5><input type="text" value={formData.hero_with_values?.[`value${num}_label`] || ''} onChange={(e) => handleInputChange(`hero_with_values.value${num}_label`, e.target.value)} placeholder="Label" className="w-full px-4 py-3 border rounded-lg mb-2" /><textarea value={formData.hero_with_values?.[`value${num}_description`] || ''} onChange={(e) => handleInputChange(`hero_with_values.value${num}_description`, e.target.value)} rows={2} placeholder="Description" className="w-full px-4 py-3 border rounded-lg" /></div>))}
                    </div>
                    <FileUploadArea fieldName="hero_with_values_image" label="Hero Image" accept="image/*" dimensions="800x600px" aspectRatio="4:3" />
                  </div>
                </div>
              )}

              {/* Category Showcase */}
              {activeSection === 'category_showcase' && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">Category Showcase</h2>
                  </div>
                  <input type="text" value={formData.category_showcase?.title || ''} onChange={(e) => handleInputChange('category_showcase.title', e.target.value)} placeholder="Section Title" className="w-full px-4 py-3 border rounded-lg mb-4" />
                  <textarea value={formData.category_showcase?.description || ''} onChange={(e) => handleInputChange('category_showcase.description', e.target.value)} rows={3} placeholder="Description" className="w-full px-4 py-3 border rounded-lg mb-4" />
                  {[1, 2, 3].map((num) => (<div key={num} className="bg-gray-50 p-6 rounded-lg mb-4"><h5>Category {num}</h5><input type="text" value={formData.category_showcase?.[`category${num}_title`] || ''} onChange={(e) => handleInputChange(`category_showcase.category${num}_title`, e.target.value)} placeholder="Category Title" className="w-full px-4 py-3 border rounded-lg mb-2" /><FileUploadArea fieldName={`category_showcase${num}_image`} label="Category Image" accept="image/*" dimensions="400x500px" aspectRatio="4:5" /></div>))}
                </div>
              )}

              {/* Remaining sections in compact form */}
              {activeSection === 'craftsmanship_story' && (<div className="space-y-6"><h2 className="text-2xl font-bold mb-4">Craftsmanship Story</h2><input type="text" value={formData.craftsmanship_story?.title || ''} onChange={(e) => handleInputChange('craftsmanship_story.title', e.target.value)} placeholder="Title" className="w-full px-4 py-3 border rounded-lg mb-4" /><textarea value={formData.craftsmanship_story?.description || ''} onChange={(e) => handleInputChange('craftsmanship_story.description', e.target.value)} rows={4} placeholder="Description" className="w-full px-4 py-3 border rounded-lg" /></div>)}
              {activeSection === 'video_story' && (<div className="space-y-6"><h2 className="text-2xl font-bold mb-4">Video Story</h2><input type="text" value={formData.video_story?.title || ''} onChange={(e) => handleInputChange('video_story.title', e.target.value)} placeholder="Title" className="w-full px-4 py-3 border rounded-lg mb-4" /><textarea value={formData.video_story?.description || ''} onChange={(e) => handleInputChange('video_story.description', e.target.value)} rows={3} placeholder="Description" className="w-full px-4 py-3 border rounded-lg" /></div>)}
              {activeSection === 'values_principles' && (<div className="space-y-6"><h2 className="text-2xl font-bold mb-4">Values & Principles</h2><input type="text" value={formData.values_principles?.title || ''} onChange={(e) => handleInputChange('values_principles.title', e.target.value)} placeholder="Title" className="w-full px-4 py-3 border rounded-lg mb-4" />{[1, 2, 3, 4].map((num) => (<div key={num} className="bg-gray-50 p-4 rounded-lg mb-4"><input type="text" value={formData.values_principles?.[`principle${num}_title`] || ''} onChange={(e) => handleInputChange(`values_principles.principle${num}_title`, e.target.value)} placeholder={`Principle ${num} Title`} className="w-full px-4 py-3 border rounded-lg mb-2" /><textarea value={formData.values_principles?.[`principle${num}_description`] || ''} onChange={(e) => handleInputChange(`values_principles.principle${num}_description`, e.target.value)} rows={2} placeholder="Description" className="w-full px-4 py-3 border rounded-lg" /></div>))}</div>)}
              {activeSection === 'category_grid' && (<div className="space-y-6"><h2 className="text-2xl font-bold mb-4">Category Grid</h2><input type="text" value={formData.category_grid?.title || ''} onChange={(e) => handleInputChange('category_grid.title', e.target.value)} placeholder="Title" className="w-full px-4 py-3 border rounded-lg mb-4" />{[1, 2, 3, 4].map((num) => (<div key={num} className="bg-gray-50 p-4 rounded-lg mb-4"><input type="text" value={formData.category_grid?.[`category${num}_title`] || ''} onChange={(e) => handleInputChange(`category_grid.category${num}_title`, e.target.value)} placeholder={`Category ${num}`} className="w-full px-4 py-3 border rounded-lg" /></div>))}</div>)}
              {activeSection === 'quality_values' && (<div className="space-y-6"><h2 className="text-2xl font-bold mb-4">Quality Values</h2><input type="text" value={formData.quality_values?.title || ''} onChange={(e) => handleInputChange('quality_values.title', e.target.value)} placeholder="Title" className="w-full px-4 py-3 border rounded-lg mb-4" /><textarea value={formData.quality_values?.description || ''} onChange={(e) => handleInputChange('quality_values.description', e.target.value)} rows={3} placeholder="Description" className="w-full px-4 py-3 border rounded-lg" /></div>)}
              {activeSection === 'magazine_hero' && (<div className="space-y-6"><h2 className="text-2xl font-bold mb-4">Magazine Hero</h2><input type="text" value={formData.magazine_hero?.title || ''} onChange={(e) => handleInputChange('magazine_hero.title', e.target.value)} placeholder="Title" className="w-full px-4 py-3 border rounded-lg mb-4" /><textarea value={formData.magazine_hero?.description || ''} onChange={(e) => handleInputChange('magazine_hero.description', e.target.value)} rows={3} placeholder="Description" className="w-full px-4 py-3 border rounded-lg mb-4" /><FileUploadArea fieldName="magazine_hero_image" label="Hero Image" accept="image/*" dimensions="1200x800px" aspectRatio="3:2" /></div>)}
              {activeSection === 'editorial_stories' && (<div className="space-y-6"><h2 className="text-2xl font-bold mb-4">Editorial Stories</h2><input type="text" value={formData.editorial_stories?.title || ''} onChange={(e) => handleInputChange('editorial_stories.title', e.target.value)} placeholder="Title" className="w-full px-4 py-3 border rounded-lg mb-4" />{[1, 2, 3].map((num) => (<div key={num} className="bg-gray-50 p-4 rounded-lg mb-4"><input type="text" value={formData.editorial_stories?.[`story${num}_title`] || ''} onChange={(e) => handleInputChange(`editorial_stories.story${num}_title`, e.target.value)} placeholder={`Story ${num} Title`} className="w-full px-4 py-3 border rounded-lg" /></div>))}</div>)}
              {activeSection === 'inspirational_quote' && (<div className="space-y-6"><h2 className="text-2xl font-bold mb-4">Inspirational Quote</h2><textarea value={formData.inspirational_quote?.quote || ''} onChange={(e) => handleInputChange('inspirational_quote.quote', e.target.value)} rows={3} placeholder="Quote" className="w-full px-4 py-3 border rounded-lg mb-4" /><input type="text" value={formData.inspirational_quote?.author || ''} onChange={(e) => handleInputChange('inspirational_quote.author', e.target.value)} placeholder="Author" className="w-full px-4 py-3 border rounded-lg" /></div>)}
              {activeSection === 'artist_hero' && (<div className="space-y-6"><h2 className="text-2xl font-bold mb-4">Artist Hero</h2><input type="text" value={formData.artist_hero?.title || ''} onChange={(e) => handleInputChange('artist_hero.title', e.target.value)} placeholder="Title" className="w-full px-4 py-3 border rounded-lg mb-4" /><textarea value={formData.artist_hero?.description || ''} onChange={(e) => handleInputChange('artist_hero.description', e.target.value)} rows={3} placeholder="Description" className="w-full px-4 py-3 border rounded-lg mb-4" /><FileUploadArea fieldName="artist_hero_image" label="Hero Image" accept="image/*" dimensions="1200x800px" aspectRatio="3:2" /></div>)}
              {activeSection === 'portfolio_gallery' && (<div className="space-y-6"><h2 className="text-2xl font-bold mb-4">Portfolio Gallery</h2><input type="text" value={formData.portfolio_gallery?.title || ''} onChange={(e) => handleInputChange('portfolio_gallery.title', e.target.value)} placeholder="Title" className="w-full px-4 py-3 border rounded-lg mb-4" /><textarea value={formData.portfolio_gallery?.description || ''} onChange={(e) => handleInputChange('portfolio_gallery.description', e.target.value)} rows={3} placeholder="Description" className="w-full px-4 py-3 border rounded-lg" /></div>)}
              {activeSection === 'artist_statement' && (<div className="space-y-6"><h2 className="text-2xl font-bold mb-4">Artist Statement</h2><input type="text" value={formData.artist_statement?.title || ''} onChange={(e) => handleInputChange('artist_statement.title', e.target.value)} placeholder="Title" className="w-full px-4 py-3 border rounded-lg mb-4" /><textarea value={formData.artist_statement?.statement || ''} onChange={(e) => handleInputChange('artist_statement.statement', e.target.value)} rows={5} placeholder="Statement" className="w-full px-4 py-3 border rounded-lg mb-4" /><FileUploadArea fieldName="artist_statement_image" label="Image" accept="image/*" dimensions="800x600px" aspectRatio="4:3" /></div>)}
              {activeSection === 'studio_contact' && (<div className="space-y-6"><h2 className="text-2xl font-bold mb-4">Studio Contact</h2><input type="text" value={formData.studio_contact?.title || ''} onChange={(e) => handleInputChange('studio_contact.title', e.target.value)} placeholder="Title" className="w-full px-4 py-3 border rounded-lg mb-4" /><input type="text" value={formData.studio_contact?.address || ''} onChange={(e) => handleInputChange('studio_contact.address', e.target.value)} placeholder="Address" className="w-full px-4 py-3 border rounded-lg mb-4" /><input type="email" value={formData.studio_contact?.email || ''} onChange={(e) => handleInputChange('studio_contact.email', e.target.value)} placeholder="Email" className="w-full px-4 py-3 border rounded-lg mb-4" /><input type="tel" value={formData.studio_contact?.phone || ''} onChange={(e) => handleInputChange('studio_contact.phone', e.target.value)} placeholder="Phone" className="w-full px-4 py-3 border rounded-lg" /></div>)}

              {/* Navigation Buttons */}
              {sections.length > 1 && (
                <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between items-center">
                  <button
                    onClick={() => {
                      const currentIndex = sections.findIndex(s => s.id === activeSection);
                      if (currentIndex > 0) {
                        setActiveSection(sections[currentIndex - 1].id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    disabled={sections.findIndex(s => s.id === activeSection) === 0}
                    className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">Previous Section</span>
                  </button>

                  <div className="text-sm text-gray-600 font-medium">
                    Section {sections.findIndex(s => s.id === activeSection) + 1} of {sections.length}
                  </div>

                  <button
                    onClick={() => {
                      const currentIndex = sections.findIndex(s => s.id === activeSection);
                      if (currentIndex < sections.length - 1) {
                        setActiveSection(sections[currentIndex + 1].id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    disabled={sections.findIndex(s => s.id === activeSection) === sections.length - 1}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="font-medium">
                      {sections.findIndex(s => s.id === activeSection) === sections.length - 1 ? 'Last Section' : 'Next Section'}
                    </span>
                    {sections.findIndex(s => s.id === activeSection) < sections.length - 1 ? (
                      <ArrowRight className="w-5 h-5" />
                    ) : (
                      <Check className="w-5 h-5" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <SuccessModal />
      <ErrorModal />
    </div>
  );

  return <Base Content={FormContent} />;
};

export default MainPageForm;
